export const API_BASE = "/api";

let tokenGetter: (() => Promise<string>) | null = null;

/**
 * Set the token getter function used to obtain authentication tokens.
 * Called in App.tsx to set tokenGetter to Auth0's getAccessTokenSilently().
 *
 * @param getter - Function that returns a Promise resolving to an access token
 */
export function setTokenGetter(getter: () => Promise<string>): void {
  tokenGetter = getter;
}

/**
 * Get an authentication token using the configured token getter.
 * Called before each API request to include the token in the Authorization header.
 *
 * @returns Promise resolving to an access token string
 * @throws Error if token getter is not configured
 */
async function getToken(): Promise<string> {
  if (!tokenGetter) {
    throw new Error("Token getter not configured");
  }
  return tokenGetter();
}

/**
 * Handle HTTP response and extract JSON data or throw error.
 * Logs detailed error information to console for debugging.
 * The error handler will convert thrown errors to user-friendly messages.
 *
 * @param res - Fetch Response object
 * @returns Promise resolving to parsed JSON data of type T
 * @throws Error with status code if response is not ok
 */
async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    
    console.error("API Error Details:", {
      status: res.status,
      statusText: res.statusText,
      url: res.url,
      errorBody: text,
      timestamp: new Date().toISOString()
    });
    
    throw new Error(`API error ${res.status}`);
  }
  return (await res.json()) as T;
}

/**
 * Perform a GET request to the API.
 *
 * @param path - API endpoint path (e.g., "/categories/1")
 * @returns Promise resolving to response data of type T
 */
export async function apiGet<T>(path: string): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
  });
  return handleResponse<T>(res);
}

/**
 * Perform a POST request to the API with JSON body.
 *
 * @param path - API endpoint path
 * @param body - Request body object (will be JSON stringified)
 * @returns Promise resolving to response data of type T
 */
export async function apiPost<T, B = unknown>(path: string, body: B): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

/**
 * Perform a POST request with FormData for file uploads.
 * Used for uploading documents because JSON cannot handle binary files.
 * Content-Type header is not set - browser automatically sets it with boundary for multipart/form-data.
 *
 * @param path - API endpoint path
 * @param formData - FormData object containing file and form fields
 * @returns Promise resolving to response data of type T
 */
export async function apiPostFormData<T>(path: string, formData: FormData): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`,
    },
    body: formData,
  });
  return handleResponse<T>(res);
}

/**
 * Perform a PUT request to the API with JSON body.
 *
 * @param path - API endpoint path
 * @param body - Request body object (will be JSON stringified)
 * @returns Promise resolving to response data of type T
 */
export async function apiPut<T, B = unknown>(path: string, body: B): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

/**
 * Perform a DELETE request to the API.
 * Handles both JSON and empty (204) responses.
 *
 * @param path - API endpoint path
 * @returns Promise resolving to response data of type T, or undefined for 204 responses
 */
export async function apiDelete<T = void>(path: string): Promise<T | undefined> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const text = await res.text();

    console.error("API Error Details:", {
      status: res.status,
      statusText: res.statusText,
      url: res.url,
      errorBody: text,
      timestamp: new Date().toISOString()
    });

    throw new Error(`API error ${res.status}`);
  }

  if (res.status === 204) {
    return undefined;
  }

  const text = await res.text();
  if (!text) {
    return undefined;
  }
  return JSON.parse(text) as T;
}
